import { useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Notice,
  Status,
  type Tone,
} from '@gatecheckpro/design-system';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * Letting StageOps provision passes for this event.
 *
 * The organizer's half of the event link (docs/contracts/stageops-interop-v1.md).
 * An invitation names the StageOps organization it is for and produces a code
 * that is shown exactly once; StageOps redeems it, and from then on the passes
 * it asks for appear in the list above like any other — issued, replaced and
 * stopped through the same rules, with "via StageOps" as their provenance.
 *
 * Worded so the organizer knows what they are granting: not a login, not a
 * role, and not access to anything but this one event's operational passes.
 */
export interface StageOpsLink {
  readonly linkId: string;
  readonly status: 'INVITED' | 'ACTIVE' | 'RETIRED';
  readonly stageops: { readonly organizationId: string; readonly eventProjectId: string | null };
  readonly invitedAt: string;
  readonly expiresAt: string;
  readonly activatedAt: string | null;
  readonly retiredAt: string | null;
  readonly retiredReason: string | null;
}

const LINK_STATES: Record<StageOpsLink['status'], { label: string; tone: Tone }> = {
  INVITED: { label: 'Invited — waiting for StageOps', tone: 'info' },
  ACTIVE: { label: 'Linked', tone: 'positive' },
  RETIRED: { label: 'Retired', tone: 'neutral' },
};

export function StageOpsLinksPanel(props: { eventId: string; organizationId: string }) {
  const base = `/api/v1/organizer/events/${props.eventId}/stageops-links`;
  const scoped = `${base}?organizationId=${props.organizationId}`;
  const { resource: links, reload } = useResource<{ links: StageOpsLink[] }>(scoped);
  const command = useCommand<{ linkId: string; invitationCode: string; expiresAt: string }>();
  const retire = useCommand<{ link: StageOpsLink }>();

  const [inviting, setInviting] = useState(false);
  const [stageopsOrganizationId, setStageopsOrganizationId] = useState('');
  const [expiresInHours, setExpiresInHours] = useState('72');
  const [issued, setIssued] = useState<{ code: string; expiresAt: string } | null>(null);

  async function invite(): Promise<void> {
    const result = await command.run(`${base}/invitations?organizationId=${props.organizationId}`, {
      method: 'POST',
      body: {
        stageopsOrganizationId: stageopsOrganizationId.trim(),
        expiresInHours: Number(expiresInHours),
      },
    });
    if (result !== null) {
      setIssued({ code: result.invitationCode, expiresAt: result.expiresAt });
      setInviting(false);
      setStageopsOrganizationId('');
      reload();
    }
  }

  async function retireLink(link: StageOpsLink): Promise<void> {
    const reason = window.prompt('Why is this StageOps link being retired?');
    if (reason === null || reason.trim() === '') return;
    const result = await retire.run(
      `${base}/${link.linkId}/retire?organizationId=${props.organizationId}`,
      { method: 'POST', body: { reason: reason.trim() } },
    );
    if (result !== null) reload();
  }

  if (links.status === 'loading') return <p>Loading StageOps links…</p>;
  if (links.status === 'failed') return <FailureNotice error={links.error} />;

  return (
    <Card title="StageOps" eyebrow="Workforce passes provisioned by another CABS product">
      <p>
        Linking this event lets a StageOps production company ask GateCheckPro for operational
        passes for the crew it assigns. GateCheckPro still decides every pass and every door:
        StageOps can request, replace and stop the passes it asked for, and read whether they were
        admitted. It gets no login here and sees nothing about tickets or attendees.
      </p>

      {issued !== null && (
        <Notice tone="caution" title="Give this code to the StageOps coordinator">
          <p data-testid="stageops-invitation-code">
            <code>{issued.code}</code>
          </p>
          <p className="gc-muted">
            Shown once. It expires {new Date(issued.expiresAt).toLocaleString()} and can be redeemed
            only by the StageOps organization you named.
          </p>
          <Button variant="quiet" onClick={() => setIssued(null)}>
            I have passed it on
          </Button>
        </Notice>
      )}

      {inviting ? (
        <form
          className="gc-stack"
          onSubmit={(formEvent) => {
            formEvent.preventDefault();
            void invite();
          }}
        >
          <Field
            label="StageOps organization id"
            hint="From the StageOps coordinator. Only that organization can redeem the code."
            value={stageopsOrganizationId}
            onChange={(changed) => setStageopsOrganizationId(changed.target.value)}
            required
            maxLength={200}
          />
          <Field
            label="Code valid for (hours)"
            type="number"
            min={1}
            max={168}
            value={expiresInHours}
            onChange={(changed) => setExpiresInHours(changed.target.value)}
            required
          />
          {command.error !== null && <FailureNotice error={command.error} />}
          <div className="gc-actions">
            <Button
              type="submit"
              busy={command.busy}
              disabled={stageopsOrganizationId.trim() === ''}
            >
              Create invitation
            </Button>
            <Button type="button" variant="quiet" onClick={() => setInviting(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button onClick={() => setInviting(true)}>Invite StageOps</Button>
      )}

      {links.data.links.length === 0 ? (
        <EmptyState title="Not linked to StageOps">
          <p>No StageOps production has been invited to provision passes for this event.</p>
        </EmptyState>
      ) : (
        <ul className="gc-list" data-testid="stageops-links">
          {links.data.links.map((link) => (
            <li key={link.linkId}>
              <div>
                <strong>StageOps organization {link.stageops.organizationId}</strong>{' '}
                <Status
                  tone={LINK_STATES[link.status].tone}
                  label={LINK_STATES[link.status].label}
                />
              </div>
              {link.stageops.eventProjectId !== null && (
                <p className="gc-muted">Project {link.stageops.eventProjectId}</p>
              )}
              <p className="gc-muted">
                {link.status === 'INVITED' &&
                  `Code expires ${new Date(link.expiresAt).toLocaleString()}`}
                {link.status === 'ACTIVE' &&
                  link.activatedAt !== null &&
                  `Linked ${new Date(link.activatedAt).toLocaleString()}`}
                {link.status === 'RETIRED' && (link.retiredReason ?? 'Retired')}
              </p>
              {link.status !== 'RETIRED' && (
                <div className="gc-row">
                  <Button variant="quiet" onClick={() => void retireLink(link)}>
                    Retire link
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {retire.error !== null && <FailureNotice error={retire.error} />}
    </Card>
  );
}
